import { useEffect, useLayoutEffect, useRef } from 'react';

import { useOnline } from '../hooks/useOnline';
import { useStore } from '../store';
import { useT } from '../useT';

/** The band `body` pads by while the banner is up (`ui-mobile` tokens, UX-490). */
export const OFFLINE_HEIGHT_VAR = '--akm-offline-h';

/**
 * "Sin conexión" / "Reconectando…" (UX-404).
 *
 * Going offline mid-session used to say nothing at all: the search screen sat on "Cargando…"
 * for ever, Home kept rendering its cached content as if everything were fine, and a query that
 * happened to be in the react-query cache still answered — so some things worked and some hung,
 * with no explanation for either. Both halves of the signal already existed and nothing read
 * them: the strings (`common:states.offline` / `reconnecting`) and `useSession().connected`,
 * which `AppShell` pushes into the store from Firebase `.info/connected`.
 *
 * It is deliberately a persistent banner rather than a toast — the condition lasts as long as
 * it lasts — and it is hosted at the router root, above the sheet and the blocking loader, so
 * it survives every screen the phone can be on.
 *
 * QA 2026-09-15 **UX-490**: it is `position: fixed` and opaque, and nothing reserved room for
 * it — so it sat *on* the title of the screen it was explaining (`elementsFromPoint` at its
 * centre returned the banner first and the `<h1>` second: 74 % of «Buscar», «Fila», «Perfil»
 * and of «Hola, …» was hidden, and on `/guest` the wordmark was cut in half). It stays fixed —
 * the message must survive a scroll — and publishes its measured height as `--akm-offline-h`
 * instead: `body` pads by it and the full-height boxes subtract it (`ui-mobile` base layer), so
 * while the banner is up every screen starts under it rather than behind it.
 */
export function OfflineBanner() {
  const t = useT();
  const online = useOnline();
  const ref = useRef<HTMLDivElement | null>(null);
  const connected = useStore((s) => s.connected);
  // `connected` only means anything while there is a session to be connected *to*.
  const inSession = useStore((s) => s.sid !== null);
  // The device is offline, or the device is on the network but the session's socket is not.
  const state = !online ? 'offline' : inSession && !connected ? 'reconnecting' : null;

  // Measured, not assumed: the strip is one line of copy over `env(safe-area-inset-top)`, so
  // its height differs between a notched iPhone, an Android and the `en` translation.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const height = state ? Math.round(ref.current?.getBoundingClientRect().height ?? 0) : 0;
    if (height > 0) root.style.setProperty(OFFLINE_HEIGHT_VAR, `${height}px`);
    else root.style.removeProperty(OFFLINE_HEIGHT_VAR);
  }, [state]);

  // A rotation or a keyboard can reflow the strip while it is up.
  useEffect(() => {
    const node = ref.current;
    if (!state || !node || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      const height = Math.round(node.getBoundingClientRect().height);
      if (height > 0) document.documentElement.style.setProperty(OFFLINE_HEIGHT_VAR, `${height}px`);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [state]);

  // Unmounting the banner (a route change, a sign-out) must give the band back.
  useEffect(
    () => () => {
      document.documentElement.style.removeProperty(OFFLINE_HEIGHT_VAR);
    },
    [],
  );

  if (!state) return null;
  return (
    <div
      ref={ref}
      role="status"
      aria-live="polite"
      data-testid="offline-banner"
      data-state={state}
      className="fixed inset-x-0 top-0 z-[70] bg-surface-3 px-4 pt-[max(6px,var(--akm-safe-top))] pb-1.5 text-center text-[13px] font-semibold text-ink"
    >
      {state === 'offline' ? t('common:states.offline') : t('common:states.reconnecting')}
    </div>
  );
}
